import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Paperclip, X, Upload, ShieldAlert, FileText, Image as ImageIcon } from "lucide-react";
import { CLAIM_REASONS } from "@/lib/claims";

const isImage = (ct) => (ct || "").startsWith("image/");

export default function FileClaimDialog({ order, accountId, onClose, onSubmitted }) {
  const fileInput = useRef(null);
  const [form, setForm] = useState({
    order_number: order?.order_number || "",
    reason: "",
    description: "",
    wholesale_value: "",
    sold_value: "",
  });
  const [attachments, setAttachments] = useState([]); // uploaded refs
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`${file.name} is over 25 MB`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        if (accountId) fd.append("account_id", accountId);
        const { data } = await api.post("/claims/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachments((a) => [...a, data]);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const removeAttachment = (id) => setAttachments((a) => a.filter((x) => x.id !== id));

  const submit = async () => {
    if (!form.order_number.trim()) return toast.error("Order number is required");
    if (!form.reason) return toast.error("Pick a reason for the claim");
    setSubmitting(true);
    try {
      const payload = {
        order_number: form.order_number.trim(),
        reason: form.reason,
        description: form.description.trim(),
        wholesale_value: Number(form.wholesale_value) || 0,
        sold_value: Number(form.sold_value) || 0,
        carrier: order?.carrier_display || order?.carrier || null,
        tracking_number: order?.tracking_number || null,
        ship_date: order?.ship_date || null,
        attachments,
      };
      if (accountId) payload.account_id = accountId;
      await api.post("/claims", payload);
      toast.success("Claim submitted — we'll keep you posted on its status.");
      onSubmitted?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto" data-testid="file-claim-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl" style={{ fontFamily: "Manrope" }}>File a claim</DialogTitle>
          <DialogDescription>Submit a claim for a lost or damaged shipment with supporting photos or documents.</DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 mt-1">
          <ShieldAlert className="w-4 h-4 text-orange-600 mt-0.5 shrink-0" />
          <p className="text-xs text-orange-800 leading-relaxed">
            Claims are reviewed and filed with the carrier. Please attach clear photos of any damage and provide accurate values.
          </p>
        </div>

        <div className="space-y-4 mt-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Order #</label>
              <input
                data-testid="claim-order-number"
                value={form.order_number}
                onChange={set("order_number")}
                placeholder="BH-100024"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Reason</label>
              <select
                data-testid="claim-reason"
                value={form.reason}
                onChange={set("reason")}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
              >
                <option value="">Select a reason…</option>
                {CLAIM_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">What happened?</label>
            <textarea
              data-testid="claim-description"
              value={form.description}
              onChange={set("description")}
              rows={3}
              placeholder="Describe the issue — condition on arrival, tracking behaviour, etc."
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Wholesale value ($)</label>
              <input
                data-testid="claim-wholesale-value"
                type="number" step="0.01" min="0"
                value={form.wholesale_value}
                onChange={set("wholesale_value")}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Sold value ($)</label>
              <input
                data-testid="claim-sold-value"
                type="number" step="0.01" min="0"
                value={form.sold_value}
                onChange={set("sold_value")}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Attachments */}
          <div>
            <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Attachments (photos / PDFs)</label>
            <button
              type="button"
              data-testid="claim-attach-btn"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 w-full justify-center border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50/50 text-slate-600 rounded-lg px-4 py-4 text-sm font-medium transition-colors disabled:opacity-60"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Uploading…" : "Click to upload photos or documents"}
            </button>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept="image/*,.pdf"
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
              data-testid="claim-file-input"
            />
            {attachments.length > 0 && (
              <ul className="mt-3 space-y-2" data-testid="claim-attachments-list">
                {attachments.map((a) => (
                  <li key={a.id} data-testid={`claim-attachment-${a.id}`} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm">
                    {isImage(a.content_type) ? <ImageIcon className="w-4 h-4 text-blue-500 shrink-0" /> : <FileText className="w-4 h-4 text-slate-500 shrink-0" />}
                    <span className="flex-1 truncate text-slate-700">{a.original_filename}</span>
                    <button onClick={() => removeAttachment(a.id)} className="text-slate-400 hover:text-red-600" data-testid={`remove-attachment-${a.id}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
          <button
            data-testid="submit-claim-btn"
            onClick={submit}
            disabled={submitting || uploading}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold px-5 py-2 rounded-md transition-colors"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
            {submitting ? "Submitting…" : "Submit claim"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
